import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Papa from 'papaparse';
import { X, Upload as UploadIcon, Trash2, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import type { Result, Gender, SplitPoint, ColumnConfig, ColumnType } from '../types';
import ColumnEditor from './ColumnEditor';
import { DEFAULT_COLUMN_CONFIG } from '../utils/defaultColumnConfig';

const VALID_GENDERS: Gender[] = ['Male', 'Female', 'Non Binary'];
const BATCH_SIZE = 5000;

const COLUMN_TYPE_HINTS = {
  rank: 'Number (e.g., 1, 2, 3) or "DNF"',
  bib_number: 'Text or number (e.g., "A123", "5001")',
  name: 'Text (e.g., "John Smith")',
  gender: 'Must be exactly: "Male", "Female", or "Non Binary"',
  gender_position: 'Number (e.g., 1, 2, 3)',
  category: 'Text (e.g., "M35", "F40", "MSEN")',
  category_position: 'Number (e.g., 1, 2, 3)',
  club: 'Text (e.g., "Running Club")',
  chip_time: 'Format: "HH:MM:SS" or "DNF"',
  gun_time: 'Format: "HH:MM:SS" or "DNF"',
  custom_1: 'Any text or number (e.g., "Team A", "247234")',
  custom_2: 'Any text or number (e.g., "Sponsor Name", "12345")',
  split_point: 'Format: "HH:MM:SS" or "DNF"'
};

interface ValidationError {
  row?: number;
  column?: string;
  message: string;
}

export default function Upload() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [success, setSuccess] = useState(false);
  const [splitPoints, setSplitPoints] = useState<SplitPoint[]>([]);
  const [currentFile, setCurrentFile] = useState<{
    name: string;
    resultCount: number;
  } | null>(null);
  const [progress, setProgress] = useState<{
    current: number;
    total: number;
    stage: string;
  }>({ current: 0, total: 0, stage: '' });
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([]);
  const [selectedColumnType, setSelectedColumnType] = useState<ColumnType | null>(null);

  useEffect(() => {
    if (eventId) {
      loadSplitPoints();
      checkExistingResults();
      loadColumnConfig();
    }
  }, [eventId]);

  useEffect(() => {
    if (splitPoints.length > 0 && columnConfig.length > 0) {
      const currentSplitColumns = columnConfig.filter(col => col.type === 'split_point');
      const currentSplitIds = currentSplitColumns.map(col => col.splitPointId);
      
      const newSplitIds = splitPoints.map(point => point.id);

      const needsUpdate = 
        currentSplitColumns.length !== splitPoints.length ||
        !currentSplitIds.every(id => newSplitIds.includes(id));

      if (needsUpdate) {
        let newConfig = columnConfig.filter(col => col.type !== 'split_point');

        splitPoints.forEach((point, index) => {
          newConfig.push({
            id: `split-${point.id}`,
            type: 'split_point',
            csvHeader: point.name.toLowerCase().replace(/\s+/g, '_'),
            displayName: point.name,
            visible: true,
            splitPointId: point.id
          });
        });

        handleColumnConfigChange(newConfig);
      }
    }
  }, [splitPoints, columnConfig]);

  const loadColumnConfig = async () => {
    if (!eventId) return;

    try {
      const { data: event } = await supabase
        .from('events')
        .select('column_config')
        .eq('id', eventId)
        .single();

      if (event?.column_config) {
        // Ensure visibleOnMobile property is properly set for all columns
        const updatedConfig = event.column_config.map((col: ColumnConfig) => ({
          ...col,
          visibleOnMobile: col.visibleOnMobile === true ? true : 
            (col.visibleOnMobile === undefined ? ['rank', 'name', 'chip_time'].includes(col.type) : false)
        }));
        setColumnConfig(updatedConfig);
      } else {
        // Use default column configuration if none exists
        setColumnConfig(DEFAULT_COLUMN_CONFIG);
      }
    } catch (err) {
      console.error('Error loading column config:', err);
      // Use default column configuration on error
      setColumnConfig(DEFAULT_COLUMN_CONFIG);
    }
  };

  const handleColumnConfigChange = async (newConfig: ColumnConfig[]) => {
    if (!eventId) return;

    try {
      const { error } = await supabase
        .from('events')
        .update({ column_config: newConfig })
        .eq('id', eventId);

      if (error) throw error;
      setColumnConfig(newConfig);
    } catch (err) {
      console.error('Error updating column config:', err);
    }
  };

  const checkExistingResults = async () => {
    if (!eventId) return;

    try { 
      const { count } = await supabase
        .from('results')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', eventId);

      if (count && count > 0) {
        // Use the new function to get the current results file
        const { data: fileData, error: fileError } = await supabase.rpc(
          'get_current_results_file',
          { p_event_id: eventId }
        );

        if (fileError) throw fileError;

        setCurrentFile({
          name: fileData?.[0]?.name?.split('/').pop() || 'Existing Results',
          resultCount: count
        });
      }
    } catch (err: any) {
      setError(`Error checking existing results: ${err.message}`);
    }
  };

  const loadSplitPoints = async () => {
    if (!eventId) return;
    
    try {
      const { data, error } = await supabase 
        .from('split_points')
        .select('*')
        .eq('event_id', eventId)
        .order('order_index', { ascending: true });

      if (error) throw error;
      if (data) {
        setSplitPoints(data);
      }
    } catch (err: any) {
      setError(`Error loading split points: ${err.message}`);
    }
  };

  const validateGender = (gender: string): Gender => {
    if (!gender?.trim()) {
      return null;
    }
    
    const normalizedGender = gender.trim();
    const validGender = VALID_GENDERS.find(
      g => g.toLowerCase() === normalizedGender.toLowerCase()
    );
    
    if (normalizedGender && !validGender) {
      throw new Error(
        `Invalid gender: "${gender}". Must be exactly one of: ${VALID_GENDERS.join(', ')}. ` +
        'Check for extra spaces and exact spelling.'
      );
    }
    return validGender;
  };

  const validateTime = (time: string | undefined): string | null => {
    if (!time?.trim()) return null;
    
    const timeStr = time.trim().toUpperCase();
    if (timeStr === 'DNF') return 'DNF';
    
    if (!/^\d{1,2}:\d{2}:\d{2}$/.test(timeStr)) {
      throw new Error('Invalid time format. Must be HH:MM:SS or DNF');
    }
    
    return timeStr;
  };

  const validateRank = (rank: string | undefined): number | null => {
    if (!rank?.trim()) return null;
    
    const rankStr = rank.trim().toUpperCase();
    if (rankStr === 'DNF') return null;
    
    const rankNum = parseInt(rankStr);
    if (isNaN(rankNum)) {
      throw new Error('Invalid rank. Must be a number or DNF');
    }
    
    return rankNum;
  };

  const validateRequiredColumns = (headers: string[], columnConfig: ColumnConfig[]) => {
    const normalizedHeaders = headers.map(h => h.toLowerCase().trim());

    const requiredColumns = columnConfig.filter(col => 
      ['name'].includes(col.type)
    );

    const missingColumns = requiredColumns.filter(col => 
      !normalizedHeaders.includes(col.csvHeader.toLowerCase().trim())
    );

    if (missingColumns.length > 0) {
      throw {
        message: 'Missing required columns',
        details: missingColumns.map(col => ({
          column: col.csvHeader,
          message: `Missing required column "${col.displayName}"`
        }))
      };
    }
  };

  const processResultsBatch = async (results: any[], startIndex: number, columnConfig: ColumnConfig[]) => {
    const batchErrors: ValidationError[] = [];
    const batchResults = results.slice(startIndex, startIndex + BATCH_SIZE).map((row: any, index: number) => {
      try {
        const mappedResult: any = {
          event_id: eventId,
          upload_order_index: startIndex + index
        };

        columnConfig.forEach(col => {
          const normalizedHeader = col.csvHeader.toLowerCase().trim();
          const value = Object.entries(row).find(
            ([key]) => key.toLowerCase().trim() === normalizedHeader
          )?.[1]?.trim();
          
          if (!value && ['name'].includes(col.type)) {
            throw {
              row: startIndex + index + 2,
              column: col.displayName,
              message: `Missing required value for ${col.displayName}`
            };
          }

          try {
            switch (col.type) {
              case 'bib_number':
                mappedResult.bib_number = value || '';
                break;
              case 'name':
                mappedResult.name = value;
                break;
              case 'gender':
                mappedResult.gender = value ? validateGender(value) : null;
                break;
              case 'gender_position':
                mappedResult.gender_position = value ? parseInt(value) : null;
                if (value && isNaN(mappedResult.gender_position)) {
                  throw new Error('Must be a number');
                }
                break;
              case 'category':
                mappedResult.category = value || null;
                break;
              case 'category_position':
                mappedResult.category_position = value ? parseInt(value) : null;
                if (value && isNaN(mappedResult.category_position)) {
                  throw new Error('Must be a number');
                }
                break;
              case 'chip_time':
                mappedResult.chip_time = validateTime(value);
                break;
              case 'gun_time':
                mappedResult.gun_time = validateTime(value);
                break;
              case 'rank':
                mappedResult.rank = validateRank(value);
                break;
              case 'club':
                mappedResult.club = value || null;
                break;
              case 'custom_1':
                mappedResult.custom_1 = value || null;
                break;
              case 'custom_2':
                mappedResult.custom_2 = value || null;
                break;
            }
          } catch (err: any) {
            if (['name'].includes(col.type)) {
              throw {
                row: startIndex + index + 2,
                column: col.displayName,
                message: err.message
              };
            }
            console.warn(`Validation failed for optional field ${col.type}:`, err);
          }
        });

        return {
          result: mappedResult,
          splitTimes: columnConfig
            .filter(col => col.type === 'split_point' && col.splitPointId)
            .map(col => {
              try {
                const normalizedHeader = col.csvHeader.toLowerCase().trim();
                const value = Object.entries(row).find(
                  ([key]) => key.toLowerCase().trim() === normalizedHeader
                )?.[1]?.trim();
                const splitTime = validateTime(value);
                return {
                  split_point_id: col.splitPointId!,
                  split_time: splitTime
                };
              } catch (err: any) {
                return null;
              }
            })
            .filter(Boolean)
        };
      } catch (err: any) {
        if (err.row) {
          batchErrors.push(err);
        } else {
          batchErrors.push({
            row: startIndex + index + 2,
            message: err.message
          });
        }
        return null;
      }
    }).filter(Boolean);

    if (batchErrors.length > 0) {
      throw {
        message: 'Validation errors found',
        errors: batchErrors
      };
    }

    const { data: insertedResults, error: resultsError } = await supabase
      .from('results')
      .insert(batchResults.map(br => br.result))
      .select('id');

    if (resultsError) throw resultsError;

    if (insertedResults) {
      const splitTimesData = insertedResults.flatMap((result, index) => 
        batchResults[index].splitTimes.map(st => ({
          result_id: result.id,
          split_point_id: st.split_point_id,
          split_time: st.split_time
        }))
      );

      if (splitTimesData.length > 0) {
        const { error: splitTimesError } = await supabase
          .from('split_times')
          .insert(splitTimesData);

        if (splitTimesError) throw splitTimesError;
      }
    }

    return batchResults.length;
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setError(null);
    setErrors([]);
    setSuccess(false);
    setProgress({ current: 0, total: 0, stage: 'Parsing CSV file...' });

    try {
      const fileName = `${Date.now()}-${file.name}`;
      const { error: uploadError } = await supabase.storage
        .from('original-results')
        .upload(`${eventId}/${fileName}`, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) throw uploadError;

      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (header) => header.trim().toLowerCase(),
        complete: async (results) => {
          try {
            validateRequiredColumns(results.meta.fields || [], columnConfig);

            if (results.errors.length > 0) {
              throw {
                message: 'CSV parsing errors',
                errors: results.errors.map(err => ({
                  row: err.row + 2,
                  message: err.message
                }))
              };
            }

            if (currentFile) {
              const { error: deleteError } = await supabase
                .from('results')
                .delete()
                .eq('event_id', eventId);

              if (deleteError) throw deleteError;

              await supabase.storage
                .from('original-results')
                .remove([`${eventId}/${currentFile.name}`]);
            }

            setProgress({ 
              current: 0, 
              total: results.data.length,
              stage: 'Uploading results...'
            });

            for (let i = 0; i < results.data.length; i += BATCH_SIZE) {
              try {
                const processedCount = await processResultsBatch(results.data, i, columnConfig);
                setProgress(prev => ({
                  ...prev,
                  current: Math.min(prev.current + processedCount, prev.total)
                }));
              } catch (err: any) {
                if (err.errors) {
                  setErrors(prev => [...prev, ...err.errors]);
                } else {
                  throw err;
                }
              }
            }

            if (errors.length === 0) {
              setCurrentFile({
                name: fileName,
                resultCount: results.data.length
              });
              setSuccess(true);
            } else {
              await supabase.storage
                .from('original-results')
                .remove([fileName]);
            }
          } catch (err: any) {
            await supabase.storage
              .from('original-results')
              .remove([fileName]);
            
            if (err.errors) {
              setErrors(err.errors);
            } else {
              setError(err.message);
            }
          } finally {
            setUploading(false);
          }
        },
        error: (err) => {
          setError(`CSV parsing error: ${err.message}`);
          setUploading(false);
        }
      });
    } catch (err: any) {
      setError(`Upload failed: ${err.message}`);
      setUploading(false);
    }
  };

  const deleteExistingResults = async () => {
    if (!eventId) return;
    setUploading(true);
    setError(null);
    setProgress({ current: 0, total: 0, stage: 'Deleting existing results...' });

    try {
      const { error: deleteError } = await supabase
        .from('results')
        .delete()
        .eq('event_id', eventId); 

      if (deleteError) throw deleteError;

      // Only attempt to remove the file if we have a name
      if (currentFile?.name) {
        const filePath = currentFile.name.includes('/') 
          ? currentFile.name 
          : `${eventId}/${currentFile.name}`;
          
        const { error: storageError } = await supabase.storage
          .from('original-results')
          .remove([filePath]);

        if (storageError) {
          console.warn('Error removing file:', storageError);
          // Continue even if file removal fails
        }
      }

      setCurrentFile(null);
      setSuccess(false);
    } catch (err: any) {
      setError(`Failed to delete results: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">Upload Results</h1>

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 whitespace-pre-wrap">{error}</div>
        </div>
      )}

      {errors.length > 0 && (
        <div className="bg-red-50 text-red-600 p-4 rounded-md mb-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5" />
            <h3 className="font-medium">Validation Errors</h3>
          </div>
          <div className="space-y-1 ml-7">
            {errors.map((err, index) => (
              <p key={index} className="text-sm">
                {err.row && <span className="font-medium">Row {err.row}: </span>}
                {err.column && <span className="font-medium">{err.column}: </span>}
                {err.message}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow space-y-6">
        {currentFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg">
              <div>
                <p className="font-medium text-gray-900">{currentFile.name}</p>
                <p className="text-sm text-gray-600">
                  {currentFile.resultCount} result{currentFile.resultCount !== 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={deleteExistingResults}
                disabled={uploading}
                className="text-red-600 hover:text-red-700 p-1 disabled:opacity-50"
                title="Delete results"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>

            {success && (
              <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg">
                <CheckCircle2 className="w-5 h-5" />
                <span>Results uploaded successfully!</span>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Update Results
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                disabled={uploading}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  disabled:opacity-50"
              />
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Choose File
            </label>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              disabled={uploading}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100
                disabled:opacity-50"
            />
          </div>
        )}

        {uploading && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
              {progress.stage}
            </div>
            {progress.total > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${(progress.current / progress.total) * 100}%` }}
                />
              </div>
            )}
            <div className="text-sm text-gray-600">
              Processed {progress.current} of {progress.total} results
            </div>
          </div>
        )}

        <div className="border-t border-gray-200 pt-6">
          <ColumnEditor
            eventId={eventId || ''}
            splitPoints={splitPoints}
            config={columnConfig}
            onChange={handleColumnConfigChange}
            onColumnTypeSelect={setSelectedColumnType}
          />

          {selectedColumnType && COLUMN_TYPE_HINTS[selectedColumnType] && (
            <div className="mt-2 flex items-start gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-medium">Format: </span>
                {COLUMN_TYPE_HINTS[selectedColumnType]}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}